<template>
  <main-template>
    <v-row align="center" class="mb-2">
      <v-col>
        <h1 class="text-h5 font-weight-bold d-flex align-center">
          <v-btn color="primary" x-small text class="mr-2" @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          Módulo de Férias — Gestão de Recursos Humanos
        </h1>
      </v-col>
      <v-col cols="auto">
        <v-btn color="primary" class="font-weight-bold" @click="abrirModalNova">
          <v-icon left>mdi-plus</v-icon>
          Cadastrar Férias
        </v-btn>
      </v-col>
    </v-row>

    <v-card outlined class="mb-4">
      <v-card-text>
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="4">
            <v-text-field
              v-model="filtros.search"
              label="Buscar colaborador"
              placeholder="Nome ou e-mail..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              @keyup.enter="carregarFerias"
              @click:clear="carregarFerias"
            />
          </v-col>
          <v-col cols="12" sm="3" md="3">
            <v-select
              v-model="filtros.status"
              :items="opcoesStatusFiltro"
              label="Status"
              outlined
              dense
              hide-details
              @change="carregarFerias"
            />
          </v-col>
          <v-col cols="12" sm="3" md="3">
            <v-autocomplete
              v-model="filtros.colaborador_id"
              :items="colaboradores"
              item-text="name"
              item-value="id"
              label="Colaborador"
              outlined
              dense
              hide-details
              clearable
              @change="carregarFerias"
            />
          </v-col>
          <v-col cols="12" sm="2" md="2" class="d-flex justify-end">
            <v-btn color="primary" outlined dense class="w-100" @click="carregarFerias">
              <v-icon left>mdi-filter-variant</v-icon>
              Filtrar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="ferias"
        :loading="carregando"
        no-data-text="Nenhum registro de férias encontrado"
        loading-text="Carregando férias..."
        class="elevation-0"
      >
        <template #[`item.colaborador`]="{ item }">
          <div class="font-weight-bold">{{ item.colaborador?.name || "—" }}</div>
          <small class="grey--text">{{ item.colaborador?.email || "" }}</small>
        </template>

        <template #[`item.periodo`]="{ item }">
          <div>
            <v-icon small class="mr-1" color="primary">mdi-calendar-range</v-icon>
            {{ formatarData(item.data_inicio) }} até {{ formatarData(item.data_fim) }}
          </div>
          <small class="grey--text font-weight-medium">
            ({{ calcularDias(item.data_inicio, item.data_fim) }} dias)
          </small>
        </template>

        <template #[`item.tipo`]="{ item }">
          <v-chip x-small outlined color="info" class="text-capitalize">
            {{ formatarTipo(item.tipo) }}
          </v-chip>
        </template>

        <template #[`item.status`]="{ item }">
          <v-chip small :color="corStatus(item.status)" dark class="font-weight-bold">
            {{ formatarStatus(item.status) }}
          </v-chip>
        </template>

        <template #[`item.observacoes`]="{ item }">
          <span>{{ item.observacoes || "—" }}</span>
        </template>

        <template #[`item.acoes`]="{ item }">
          <v-btn icon small color="primary" title="Editar" @click="abrirModalEditar(item)">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon small color="error" title="Excluir" @click="confirmarExclusao(item)">
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="modalForm" max-width="650px" persistent>
      <v-card>
        <v-card-title class="headline primary white--text">
          <v-icon left color="white">mdi-beach</v-icon>
          {{ form.id ? "Editar Cadastro de Férias" : "Cadastrar Férias para Colaborador" }}
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert v-if="erroForm" type="error" dismissible dense class="mb-4">
            {{ erroForm }}
          </v-alert>

          <v-form ref="formRef" v-model="formValido">
            <v-row dense>
              <v-col cols="12">
                <v-autocomplete
                  v-model="form.colaborador_id"
                  :items="colaboradores"
                  item-text="name"
                  item-value="id"
                  label="Colaborador *"
                  outlined
                  :rules="rulesColaborador"
                  :disabled="!!form.id"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <e-date-customize
                  v-model="form.data_inicio"
                  label="Data Inicial *"
                  outlined
                  dense
                  :rules="rulesDataInicio"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <e-date-customize
                  v-model="form.data_fim"
                  label="Data Final *"
                  outlined
                  dense
                  :rules="rulesDataFim"
                  :min="form.data_inicio || undefined"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.tipo"
                  :items="opcoesTipo"
                  label="Tipo de Férias"
                  outlined
                  dense
                />
              </v-col>

              <v-col cols="12" sm="6">
                <v-select
                  v-model="form.status"
                  :items="opcoesStatus"
                  label="Status"
                  outlined
                  dense
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="form.observacoes"
                  label="Observações"
                  placeholder="Informações adicionais sobre o período de férias..."
                  outlined
                  dense
                  rows="2"
                />
              </v-col>

              <v-col cols="12">
                <v-expansion-panels
                  flat
                  style="border: 1px solid var(--border); border-radius: 4px"
                >
                  <v-expansion-panel>
                    <v-expansion-panel-header class="subtitle-2 grey--text text--darken-2">
                      <v-icon left small>mdi-file-cog-outline</v-icon>
                      Informações de Período Aquisitivo e Abono (Opcional)
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="pt-2">
                      <v-row dense>
                        <v-col cols="12" sm="6">
                          <e-date-customize
                            v-model="form.periodo_aquisitivo_inicio"
                            label="Período Aquisitivo — Início"
                            outlined
                            dense
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <e-date-customize
                            v-model="form.periodo_aquisitivo_fim"
                            label="Período Aquisitivo — Fim"
                            outlined
                            dense
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <e-date-customize
                            v-model="form.periodo_concessivo_inicio"
                            label="Período Concessivo — Início"
                            outlined
                            dense
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <e-date-customize
                            v-model="form.periodo_concessivo_fim"
                            label="Período Concessivo — Fim"
                            outlined
                            dense
                          />
                        </v-col>
                        <v-col cols="12" sm="6">
                          <v-text-field
                            v-model.number="form.venda_dias"
                            label="Dias Vendidos (Abono)"
                            type="number"
                            min="0"
                            max="30"
                            outlined
                            dense
                          />
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="modalForm = false" :disabled="salvando">Cancelar</v-btn>
          <v-btn color="primary" class="font-weight-bold" :loading="salvando" @click="salvar">
            Salvar Férias
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="modalExcluir" max-width="450px">
      <v-card>
        <v-card-title class="headline error--text">Confirmar Exclusão</v-card-title>
        <v-card-text>
          Deseja realmente excluir o período de férias de
          <strong>{{
            itemExcluir ? (itemExcluir.colaborador ? itemExcluir.colaborador.name : "") : ""
          }}</strong>
          ({{ formatarData(itemExcluir ? itemExcluir.data_inicio : "") }} a
          {{ formatarData(itemExcluir ? itemExcluir.data_fim : "") }})?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="modalExcluir = false" :disabled="excluindo">Cancelar</v-btn>
          <v-btn color="error" :loading="excluindo" @click="excluir">Confirmar Exclusão</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.exibir" :color="snackbar.cor" timeout="4000">
      {{ snackbar.texto }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.exibir = false">Fechar</v-btn>
      </template>
    </v-snackbar>
  </main-template>
</template>

<script>
export default {
  name: "GerenciarFeriasPage",
  data() {
    return {
      carregando: false,
      salvando: false,
      excluindo: false,
      modalForm: false,
      modalExcluir: false,
      formValido: false,
      erroForm: null,
      ferias: [],
      colaboradores: [],
      itemExcluir: null,
      filtros: {
        search: "",
        status: "",
        colaborador_id: null,
      },
      form: {
        id: null,
        colaborador_id: null,
        data_inicio: "",
        data_fim: "",
        tipo: "regular",
        status: "agendado",
        observacoes: "",
        periodo_aquisitivo_inicio: "",
        periodo_aquisitivo_fim: "",
        periodo_concessivo_inicio: "",
        periodo_concessivo_fim: "",
        venda_dias: 0,
      },
      headers: [
        { text: "Colaborador", value: "colaborador", sortable: false },
        { text: "Período das Férias", value: "periodo", sortable: false },
        { text: "Tipo", value: "tipo", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Observações", value: "observacoes", sortable: false },
        { text: "Ações", value: "acoes", sortable: false, align: "right" },
      ],
      opcoesStatusFiltro: [
        { text: "Todos os Status", value: "" },
        { text: "Agendado", value: "agendado" },
        { text: "Em Andamento", value: "em_andamento" },
        { text: "Concluído", value: "concluido" },
        { text: "Cancelado", value: "cancelado" },
      ],
      opcoesStatus: [
        { text: "Agendado", value: "agendado" },
        { text: "Em Andamento", value: "em_andamento" },
        { text: "Concluído", value: "concluido" },
        { text: "Cancelado", value: "cancelado" },
      ],
      opcoesTipo: [
        { text: "Regular (30 dias ou fracionado)", value: "regular" },
        { text: "Férias Coletivas", value: "coletivas" },
        { text: "Férias Fracionadas", value: "fracionada" },
      ],
      snackbar: {
        exibir: false,
        texto: "",
        cor: "success",
      },
    };
  },
  computed: {
    rulesColaborador() {
      return [(v) => !!v || "Selecione o colaborador"];
    },
    rulesDataInicio() {
      return [(v) => !!v || "Informe a data inicial"];
    },
    rulesDataFim() {
      const toIso = (dt) => {
        if (!dt) return "";
        if (typeof dt === "string" && dt.match(/^\d{4}-\d{2}-\d{2}/)) return dt.split("T")[0];
        if (typeof dt === "string" && dt.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          const [d, m, a] = dt.split("/");
          return `${a}-${m}-${d}`;
        }
        return dt;
      };
      return [
        (v) => !!v || "Informe a data final",
        (v) => {
          if (!this.form.data_inicio || !v) return true;
          const isoInicio = toIso(this.form.data_inicio);
          const isoFim = toIso(v);
          return isoFim >= isoInicio || "Data final deve ser maior ou igual à data inicial";
        },
      ];
    },
  },
  async mounted() {
    await Promise.all([this.carregarColaboradores(), this.carregarFerias()]);
  },
  methods: {
    async carregarColaboradores() {
      try {
        const res = await this.$services.colaboradorPontosService.listarColaboradores();
        this.colaboradores = res || [];
      } catch (e) {
        this.colaboradores = [];
      }
    },
    async carregarFerias() {
      this.carregando = true;
      try {
        const response = await this.$services.colaboradorFeriasService.listar(this.filtros);
        if (response && Array.isArray(response.data)) {
          this.ferias = response.data;
        } else if (response && response.data && Array.isArray(response.data.data)) {
          this.ferias = response.data.data;
        } else if (Array.isArray(response)) {
          this.ferias = response;
        } else {
          this.ferias = [];
        }
      } catch (e) {
        this.mostrarSnackbar("Erro ao carregar lista de férias", "error");
      } finally {
        this.carregando = false;
      }
    },
    abrirModalNova() {
      this.erroForm = null;
      this.form = {
        id: null,
        colaborador_id: null,
        data_inicio: "",
        data_fim: "",
        tipo: "regular",
        status: "agendado",
        observacoes: "",
        periodo_aquisitivo_inicio: "",
        periodo_aquisitivo_fim: "",
        periodo_concessivo_inicio: "",
        periodo_concessivo_fim: "",
        venda_dias: 0,
      };
      this.modalForm = true;
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.resetValidation();
      });
    },
    abrirModalEditar(item) {
      this.erroForm = null;
      this.form = {
        id: item.id,
        colaborador_id: item.colaborador_id,
        data_inicio: item.data_inicio,
        data_fim: item.data_fim,
        tipo: item.tipo || "regular",
        status: item.status || "agendado",
        observacoes: item.observacoes || "",
        periodo_aquisitivo_inicio: item.periodo_aquisitivo_inicio || "",
        periodo_aquisitivo_fim: item.periodo_aquisitivo_fim || "",
        periodo_concessivo_inicio: item.periodo_concessivo_inicio || "",
        periodo_concessivo_fim: item.periodo_concessivo_fim || "",
        venda_dias: item.venda_dias || 0,
      };
      this.modalForm = true;
    },
    async salvar() {
      if (this.$refs.formRef && !this.$refs.formRef.validate()) {
        return;
      }
      this.salvando = true;
      this.erroForm = null;

      try {
        if (this.form.id) {
          await this.$services.colaboradorFeriasService.atualizar(this.form.id, this.form);
          this.mostrarSnackbar("Período de férias atualizado com sucesso!");
        } else {
          await this.$services.colaboradorFeriasService.salvar(this.form);
          this.mostrarSnackbar("Férias cadastradas com sucesso!");
        }
        this.modalForm = false;
        await this.carregarFerias();
      } catch (e) {
        if (e.response?.data?.errors) {
          const msgs = Object.values(e.response.data.errors).flat();
          this.erroForm = msgs.join(" | ");
        } else if (e.response?.data?.message) {
          this.erroForm = e.response.data.message;
        } else {
          this.erroForm = "Erro ao salvar cadastro de férias.";
        }
      } finally {
        this.salvando = false;
      }
    },
    confirmarExclusao(item) {
      this.itemExcluir = item;
      this.modalExcluir = true;
    },
    async excluir() {
      if (!this.itemExcluir) return;
      this.excluindo = true;
      try {
        await this.$services.colaboradorFeriasService.excluir(this.itemExcluir.id);
        this.mostrarSnackbar("Férias excluídas com sucesso!");
        this.modalExcluir = false;
        await this.carregarFerias();
      } catch (e) {
        this.mostrarSnackbar("Erro ao excluir férias.", "error");
      } finally {
        this.excluindo = false;
      }
    },
    formatarData(dt) {
      if (!dt) return "—";
      const parts = dt.split("-");
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      return dt;
    },
    calcularDias(inicio, fim) {
      if (!inicio || !fim) return 0;
      const d1 = new Date(inicio);
      const d2 = new Date(fim);
      const diffTime = Math.abs(d2 - d1);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    },
    formatarTipo(tipo) {
      const mapa = {
        regular: "Regular",
        coletivas: "Coletivas",
        fracionada: "Fracionada",
      };
      return mapa[tipo] || tipo || "Regular";
    },
    formatarStatus(st) {
      const mapa = {
        agendado: "Agendado",
        em_andamento: "Em Andamento",
        concluido: "Concluído",
        cancelado: "Cancelado",
      };
      return mapa[st] || st;
    },
    corStatus(st) {
      const mapa = {
        agendado: "info",
        em_andamento: "success",
        concluido: "grey darken-1",
        cancelado: "error",
      };
      return mapa[st] || "primary";
    },
    mostrarSnackbar(texto, cor = "success") {
      this.snackbar.texto = texto;
      this.snackbar.cor = cor;
      this.snackbar.exibir = true;
    },
  },
};
</script>
